import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { reserveAPI, referral, cash, dateRange, nightsLabel } from "../../services/api";
import { reservationsAPI, signedIn, getMember } from "../../services/portalApi";
import Footer from "../home/footer";
import WaitlistForm from "./WaitlistForm";
import StepDates from "./steps/StepDates";
import StepRoom from "./steps/StepRoom";
import StepAddons from "./steps/StepAddons";
import StepDetails from "./steps/StepDetails";
import StepReview from "./steps/StepReview";
import Confirmation from "./steps/Confirmation";
import { startCardPayment } from "./CardPay";
import "./booking.css";
import Picture from "../utils/Picture";

const STEPS = ["Dates", "Room", "Add-ons", "Details", "Confirm"];

const blankGuest = () => ({ first_name: "", last_name: "", email: "", phone: "", country: "", diets: [], dietary_notes: "" });

/* A booking in progress survives a refresh and the trip to the email
   code, but only in this tab, and only for this date. */
const draftKey = (slug) => `makua_booking_${slug}`;
const loadDraft = (slug) => {
  try { return JSON.parse(sessionStorage.getItem(draftKey(slug)) || "null"); } catch (_) { return null; }
};
const saveDraft = (slug, d) => {
  try { sessionStorage.setItem(draftKey(slug), JSON.stringify(d)); } catch (_) { /* private mode */ }
};
const dropDraft = (slug) => {
  try { sessionStorage.removeItem(draftKey(slug)); } catch (_) { /* private mode */ }
};

const freshDraft = () => ({
  step: 1, guests: 1, before: 0, after: 0, extension_request: "",
  room_type_id: null, addons: {},
  people: [blankGuest()],
  heard_from: "", accessibility: "", room_preferences: "", special_requests: "",
  payment_plan: null, payment_method: null, accept_terms: false,
});

/**
 * Reserve a place on one dated retreat (spec 01, "Customer booking
 * sequence"): dates and party → room category → add-ons → each guest →
 * review and reserve. Prices and availability are fetched again at every
 * change, and the backend checks everything once more when the guest
 * reserves, so a room or place that goes in the meantime is caught.
 */
const BookPage = () => {
  const { slug } = useParams();
  const [d, setD] = useState(() => ({ ...freshDraft(), ...(loadDraft(slug) || {}) }));
  const [options, setOptions] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [quote, setQuote] = useState(null);
  const [quoteError, setQuoteError] = useState("");
  const [authed, setAuthed] = useState(signedIn());
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [done, setDone] = useState(null);
  const mainRef = useRef(null);

  const set = (patch) => setD((cur) => ({ ...cur, ...(typeof patch === "function" ? patch(cur) : patch) }));

  useEffect(() => { if (!done) saveDraft(slug, d); }, [slug, d, done]);

  /* Rooms and places depend on party size and the nights chosen. */
  useEffect(() => {
    let alive = true;
    reserveAPI.options(slug, { guests: d.guests, before: d.before, after: d.after })
      .then((o) => { if (alive) { setOptions(o); setLoadError(null); } })
      .catch((err) => { if (alive) setLoadError(err); });
    return () => { alive = false; };
  }, [slug, d.guests, d.before, d.after]);

  /* A room that no longer fits the party or the dates is let go of. */
  useEffect(() => {
    if (!options || !d.room_type_id) return;
    const c = options.categories.find((x) => x.id === d.room_type_id);
    if (!c || !c.fits) set({ room_type_id: null });
  }, [options]); // eslint-disable-line react-hooks/exhaustive-deps

  /* The live price */
  const addonList = useMemo(() => Object.entries(d.addons).filter(([, q]) => q > 0).map(([id, qty]) => ({ session_id: Number(id), qty })), [d.addons]);
  useEffect(() => {
    if (!options) return undefined;
    let alive = true;
    const t = setTimeout(() => {
      reserveAPI.quote(slug, {
        guests_count: d.guests, nights_before: d.before, nights_after: d.after,
        room_type_id: d.room_type_id, addons: addonList,
      })
        .then((q) => { if (alive) { setQuote(q); setQuoteError(""); } })
        .catch((err) => { if (alive) setQuoteError(err.message); });
    }, 200);
    return () => { alive = false; clearTimeout(t); };
  }, [slug, options, d.guests, d.before, d.after, d.room_type_id, addonList]);

  /* Keep one details form per guest. */
  useEffect(() => {
    if (d.people.length === d.guests) return;
    set((cur) => ({ people: Array.from({ length: cur.guests }, (_, i) => cur.people[i] || blankGuest()) }));
  }, [d.guests]); // eslint-disable-line react-hooks/exhaustive-deps

  /* The signed-in person is the lead guest. */
  useEffect(() => {
    const m = authed ? getMember() : null;
    if (!m) return;
    set((cur) => {
      const lead = cur.people[0] || blankGuest();
      return { people: [{ ...lead, first_name: lead.first_name || m.firstName || "", last_name: lead.last_name || m.lastName || "", email: m.email || lead.email }, ...cur.people.slice(1)] };
    });
  }, [authed]);

  /* The payment plan offered can change with the total; fall back to the first one. */
  useEffect(() => {
    const plans = quote?.options?.plans || [];
    if (plans.length && !plans.some((p) => p.id === d.payment_plan)) set({ payment_plan: plans[0].id });
    const methods = quote?.options?.methods || [];
    if (methods.length && !methods.includes(d.payment_method)) set({ payment_method: methods[0] });
  }, [quote]); // eslint-disable-line react-hooks/exhaustive-deps

  const retreat = options?.retreat;
  const category = options?.categories.find((c) => c.id === d.room_type_id) || null;
  const requestOnly = !!category?.requestOnly;

  const stepOk = (n) => {
    if (n === 1) return d.guests >= 1 && retreat && d.guests <= retreat.placesLeft;
    if (n === 2) return !!category && category.fits;
    if (n === 3) return !quoteError;
    if (n === 4) return authed && d.people.length === d.guests && d.people.every((g) => g.first_name.trim() && g.last_name.trim());
    if (n === 5) return d.accept_terms && (requestOnly || (d.payment_plan && d.payment_method));
    return false;
  };
  const firstBlocked = [1, 2, 3, 4].find((n) => !stepOk(n));
  const go = (n) => {
    set({ step: n });
    requestAnimationFrame(() => mainRef.current?.focus({ preventScroll: true }));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const next = () => go(Math.min(5, d.step + 1));
  const back = () => go(Math.max(1, d.step - 1));

  const reserve = async () => {
    setSaving(true); setSaveError("");
    try {
      const r = await reservationsAPI.create({
        pathway_id: retreat.id, guests_count: d.guests, nights_before: d.before, nights_after: d.after,
        extension_request: d.extension_request || undefined,
        room_type_id: d.room_type_id, addons: addonList,
        guests: d.people,
        heard_from: d.heard_from, accessibility: d.accessibility, room_preferences: d.room_preferences,
        special_requests: d.special_requests,
        payment_plan: requestOnly ? undefined : d.payment_plan, payment_method: requestOnly ? undefined : d.payment_method,
        accept_terms: d.accept_terms,
        referral_code: referral.get() || undefined,
      });
      dropDraft(slug);
      // Card: straight on to Stripe's page. If that can't start, the
      // confirmation shows the place is held and offers the card button.
      if (r.payment_method === "card" && r.status === "pending_payment" && r.cardPayments && r.payable?.now > 0) {
        try { await startCardPayment(r.ref, "now"); return; } catch (_) { /* fall through to the confirmation */ }
      }
      setDone(r);
      window.scrollTo({ top: 0 });
    } catch (err) {
      setSaveError(err.message);
      if (err.status === 401) setAuthed(false);
      // Something may have gone while they decided: refresh what's available.
      reserveAPI.options(slug, { guests: d.guests, before: d.before, after: d.after }).then(setOptions).catch(() => {});
    } finally { setSaving(false); }
  };

  /* ── Before the flow ── */
  if (done) return <Shell><Confirmation reservation={done} retreat={retreat} /></Shell>;

  if (loadError) {
    return (
      <Shell narrow>
        <h1 className="mk-title">{loadError.status === 404 ? "This retreat isn't open for booking" : "We couldn't load this retreat"}</h1>
        <p className="mk-muted">{loadError.status === 404 ? "It may have filled up, or its dates may have closed." : loadError.message}</p>
        <div className="mk-actions"><Link className="mk-btn mk-btn--primary" to="/retreats">See upcoming retreats</Link></div>
      </Shell>
    );
  }
  // Full width and full height while loading, so the steps don't jump in.
  if (!options) return <Shell><p className="mk-muted" style={{ minHeight: "100vh" }}>Loading…</p></Shell>;

  if (retreat.soldOut) {
    return (
      <Shell narrow>
        <p className="mk-eyebrow">{dateRange(retreat.startDate, retreat.endDate)}</p>
        <h1 className="mk-title">{retreat.title} is full</h1>
        {retreat.waitlist ? <WaitlistForm pathwayId={retreat.id} /> : <p className="mk-muted">Every place on this date has gone.</p>}
        {options.otherDates.length > 0 && (
          <p className="mk-muted mt-4">Or choose another date: {options.otherDates.map((o, i) => (
            <React.Fragment key={o.id}>{i > 0 && ", "}<Link to={`/book/${o.slug}`}>{dateRange(o.startDate, o.endDate)}</Link></React.Fragment>
          ))}</p>
        )}
      </Shell>
    );
  }

  const stepProps = { d, set, options, quote, category, retreat };

  return (
    <Shell>
      <header className="bk-head">
        <p className="mk-eyebrow">Book your retreat</p>
        <h1 className="bk-title hFont">{retreat.title}</h1>
        <p className="bk-sub">{dateRange(retreat.startDate, retreat.endDate)} · {nightsLabel(retreat.nights)}{retreat.location ? ` · ${retreat.location}` : ""}</p>
      </header>

      <nav aria-label="Booking steps">
        <ol className="bk-steps">
          {STEPS.map((label, i) => {
            const n = i + 1;
            const reachable = n <= d.step || (firstBlocked === undefined || n <= firstBlocked);
            const state = n === d.step ? "is-now" : n < d.step ? "is-done" : "";
            return (
              <li key={label} className={state}>
                <button type="button" onClick={() => reachable && go(n)} disabled={!reachable} aria-current={n === d.step ? "step" : undefined}>
                  <span className="bk-step-n" aria-hidden="true">{n < d.step ? "✓" : n}</span>
                  <span className="bk-step-label">{label}</span>
                </button>
              </li>
            );
          })}
        </ol>
      </nav>

      <div className="bk-layout">
        <main className="bk-main" ref={mainRef} tabIndex={-1} aria-live="polite">
          {d.step === 1 && <StepDates {...stepProps} />}
          {d.step === 2 && <StepRoom {...stepProps} />}
          {d.step === 3 && <StepAddons {...stepProps} />}
          {d.step === 4 && <StepDetails {...stepProps} authed={authed} onSignedIn={() => setAuthed(true)} />}
          {d.step === 5 && <StepReview {...stepProps} go={go} requestOnly={requestOnly} saveError={saveError} />}
          {quoteError && d.step >= 3 && <p className="mk-error mt-3" role="alert">{quoteError}</p>}

          <div className="bk-nav">
            {d.step > 1
              ? <button type="button" className="mk-btn mk-btn--ghost" onClick={back}>Back</button>
              : <Link className="mk-btn mk-btn--ghost" to={retreat.typeSlug ? `/retreats/${retreat.typeSlug}?date=${retreat.id}` : "/retreats"}>Back to retreat</Link>}
            {d.step < 5 ? (
              <button type="button" className="mk-btn mk-btn--primary" disabled={!stepOk(d.step)} onClick={next}>
                Continue to {STEPS[d.step].toLowerCase()}
              </button>
            ) : (
              <button type="button" className="mk-btn mk-btn--primary" disabled={!stepOk(5) || saving || !!quoteError} onClick={reserve}>
                {saving ? "Reserving…" : requestOnly ? "Send request" : "Reserve my place"}
              </button>
            )}
          </div>
        </main>

        <Summary d={d} retreat={retreat} category={category} options={options} quote={quote} onEdit={go} step={d.step}
          action={d.step < 5
            ? { label: `Continue to ${STEPS[d.step]?.toLowerCase()}`, disabled: !stepOk(d.step), onClick: next }
            : { label: saving ? "Reserving…" : requestOnly ? "Send request" : "Reserve my place", disabled: !stepOk(5) || saving || !!quoteError, onClick: reserve }} />
      </div>
    </Shell>
  );
};

/**
 * What they've chosen and what it costs, beside the steps on a wide
 * screen and as a bar at the foot of the screen on a phone (tap to open).
 */
const Summary = ({ d, retreat, category, options, quote, onEdit, action }) => {
  const [open, setOpen] = useState(false);
  const sessions = options.sessions;
  const chosen = Object.entries(d.addons).filter(([, q]) => q > 0)
    .map(([id, qty]) => ({ s: sessions.find((x) => x.id === Number(id)), qty })).filter((x) => x.s);
  const arrive = quote?.arrive || retreat.startDate;
  const depart = quote?.depart || retreat.endDate;
  const total = quote?.total;
  const plan = quote?.options?.plans?.find((p) => p.id === d.payment_plan);

  return (
    <aside className={`bk-summary ${open ? "is-open" : ""}`} aria-label="Your booking">
      <button type="button" className="bk-summary-bar" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <span>{open ? "Hide summary" : "Your booking"}</span>
        <b>{total != null ? cash(total) : "…"}</b>
      </button>

      <div className="bk-summary-body">
        {retreat.image && <Picture className="bk-summary-img" src={retreat.image} sizes="352px" width={360} height={200} />}
        <h2 className="bk-summary-title hFont">{retreat.title}</h2>
        <dl className="bk-facts">
          <div><dt>Stay</dt><dd>{dateRange(arrive, depart)}<small>{nightsLabel(retreat.nights + d.before + d.after)}{d.before + d.after ? `, including ${d.before + d.after} extra` : ""}</small></dd></div>
          <div><dt>Guests</dt><dd>{d.guests}</dd></div>
          <div>
            <dt>Room</dt>
            <dd>{category ? category.name : <span className="bk-muted">Not chosen yet</span>}
              {category && <button type="button" className="bk-edit" onClick={() => onEdit(2)}>Change</button>}
            </dd>
          </div>
          {chosen.length > 0 && (
            <div>
              <dt>Add-ons</dt>
              <dd>
                <ul className="bk-mini-list">{chosen.map(({ s, qty }) => <li key={s.id}>{s.name}{qty > 1 ? ` × ${qty}` : ""}</li>)}</ul>
                <button type="button" className="bk-edit" onClick={() => onEdit(3)}>Change</button>
              </dd>
            </div>
          )}
        </dl>

        {quote && (
          <table className="bk-lines">
            <caption className="visually-hidden">Price</caption>
            <tbody>
              {quote.lines.map((l, i) => (
                <tr key={i}><th scope="row">{l.label}</th><td>{l.amount === 0 ? "Included" : cash(l.amount)}</td></tr>
              ))}
              {category?.isBase && <tr><th scope="row">{category.name}</th><td>Included</td></tr>}
            </tbody>
            <tfoot>
              <tr className="bk-total"><th scope="row">Total</th><td>{cash(quote.total)}</td></tr>
              {plan && plan.id !== "full" && <tr><th scope="row">Due today</th><td>{cash(plan.dueNow)}</td></tr>}
            </tfoot>
          </table>
        )}
        {quote?.pending?.length > 0 && (
          <p className="bk-note">
            {quote.pending.map((p) => p.name).join(", ")}: {quote.pending.some((p) => p.why === "clearance") ? "charged only once you're medically cleared." : "on request; we'll confirm and price it with you."}
          </p>
        )}
        {category?.requestOnly && <p className="bk-note">{category.name} is booked by request. Nothing is charged until we've confirmed it with you.</p>}

        <button type="button" className="mk-btn mk-btn--primary bk-summary-go" disabled={action.disabled} onClick={action.onClick}>{action.label}</button>
      </div>
    </aside>
  );
};

const Shell = ({ children, narrow }) => (
  <>
    <div className="mk-page grain-bg-light bk-page">
      <div className={narrow ? "mk-wrap" : "bk-wrap"}>{children}</div>
    </div>
    <Footer />
  </>
);

/* Another date of the same retreat is a different booking: start fresh. */
const BookRoute = () => {
  const { slug } = useParams();
  return <BookPage key={slug} />;
};

export default BookRoute;
